import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  activateTournament,
  addTournamentPlayers,
  deleteTournament,
  fetchEligibleTournamentPlayers,
  fetchTournamentById,
  fetchTournaments,
  fetchPlayerCategories,
  inviteTournamentPlayers,
  createPlayerCategory,
  updatePlayerCategory,
  deletePlayerCategory,
  assignPlayersToCategory,
  removeTournamentPlayer,
  createTournamentJoinLink,
  getTournamentJoinLink,
} from '../../../services/tournamentService';
import { createTeamRegistrationInvite, disableTeamRegistrationInvite, regenerateTeamRegistrationInvite } from '../../../services/teamRegistrationService';
import { getCommunityId } from '../../../utils/community';
import {
  buildManagementScope,
  buildPlayerManagerTournamentsPath,
} from '../../../utils/managementNavigation';
import { sanitizeProfilePhoto } from '../../../utils/profile';
import PageShell from '../../../components/layout/PageShell';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { hasIndividualAccess } from '../../../utils/userRoles';


const LoadingState = ({ message }) => (
  <div className="d-flex justify-content-center py-5"> 
    <div className="text-muted">{message || 'Loading...'}</div>
  </div>
);

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const PlayerManagementPage = () => {
  const navigate = useNavigate();
  const {
    tournamentId: routeTournamentId,
    organizationId: routeOrganizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    managerId: routeManagerId,
    playerManagerId: routePlayerManagerId,
  } = useParams();
  const { token, user } = useAuth();

  const isIndividualRoute = Boolean(routeTournamentId && !routeOrganizationId && !routeCommunityId && !routeClubId);
  const ownerType = isIndividualRoute ? 'individual' : (routeOrganizationId ? 'organization' : routeCommunityId ? 'community' : routeClubId ? 'club' : 'organization');


  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user]
  );
  const isOrganizationAdmin = userRoles.has('organization_admin');
  const isPlayerManager = userRoles.has('player_manager');
  const isTournamentAdmin = userRoles.has('tournament_admin');
  const isTournamentManager = userRoles.has('tournament_manager');
  const isSuperAdmin = userRoles.has('super_admin');
  const isTeamAdmin = userRoles.has('team_admin');
  const hasTournamentAdminAccess =
    isSuperAdmin ||
    isTournamentAdmin ||
    isTournamentManager ||
    isOrganizationAdmin ||
    isTeamAdmin ||
    isPlayerManager ||
    hasIndividualAccess(user);

  const [tournaments, setTournaments] = useState([]);
  const [eligiblePlayers, setEligiblePlayers] = useState({});
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [loadingEligible, setLoadingEligible] = useState({});
  const [savingTournament, setSavingTournament] = useState({});
  const [actionStatus, setActionStatus] = useState({});
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [archiveStatus, setArchiveStatus] = useState({});
  const [archivingTournament, setArchivingTournament] = useState({});
  const [reactivatingTournament, setReactivatingTournament] = useState({});
  const [removingPlayers, setRemovingPlayers] = useState({});
  const [playerCategories, setPlayerCategories] = useState({});
  const [playerCategoryAssignments, setPlayerCategoryAssignments] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [categorySaving, setCategorySaving] = useState({});
  const [categoryDrafts, setCategoryDrafts] = useState({});
  const [categoryEdits, setCategoryEdits] = useState({});
  const [managementTabSelections, setManagementTabSelections] = useState({});

  const [tournamentJoinLinks, setTournamentJoinLinks] = useState({});
  const [tournamentJoinLinkStatus, setTournamentJoinLinkStatus] = useState({});
  const [generatingJoinLink, setGeneratingJoinLink] = useState({});
  const [teamRegistrationInvites, setTeamRegistrationInvites] = useState({});
  const [teamRegistrationStatus, setTeamRegistrationStatus] = useState({});
  const [teamRegistrationLoading, setTeamRegistrationLoading] = useState({});

  const joinLinkAutoGeneratedRef = useRef({});


  const getTournamentId = (tournament) => {
    if (!tournament) return '';

    const rawId =
      tournament.id || tournament._id?.toString?.() || tournament.tournamentId || tournament._id;

    if (!rawId) return '';

    return rawId.toString ? rawId.toString() : `${rawId}`;
  };

  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value._id) return String(value._id);
    return String(value);
  };

  const normalizedUserId = useMemo(() => normalizeId(user?.id || user?._id), [user?.id, user?._id]);

  const logApiError = useCallback((url, error, payload = {}) => {
    const status = error?.status || error?.response?.status || error?.cause?.status;
    return status;
  }, []);

  const isApprovedPlayerStatus = useCallback((status) => {
    if (!status) return true;

    return ['approved', 'active'].includes(`${status}`.trim().toLowerCase());
  }, []);
  const managerScopeId = useMemo(
    () => normalizeId(routePlayerManagerId || routeManagerId) || normalizedUserId,
    [normalizedUserId, routeManagerId, routePlayerManagerId],
  );
  const managementScope = useMemo(
    () =>
      buildManagementScope({
        communityId: routeCommunityId,
        clubId: routeClubId,
        organizationId: routeOrganizationId,
      }),
    [routeClubId, routeCommunityId, routeOrganizationId],
  );
  const workplacePath = useMemo(() => {
    if (routeCommunityId && routeTournamentId) {
      return `/community/${routeCommunityId}/managers/tournaments/${routeTournamentId}`;
    }

    if (routeClubId && routeTournamentId) {
      return `/club/${routeClubId}/managers/tournaments/${routeTournamentId}`;
    }

    if (routeOrganizationId && routeTournamentId) {
      return `/org/${routeOrganizationId}/managers/tournaments/${routeTournamentId}`;
    }

    if (isIndividualRoute && routeTournamentId) {
      return `/dashboard/individual/tournaments/${routeTournamentId}/admin`;
    }

    return buildPlayerManagerTournamentsPath(managementScope, managerScopeId) || '/dashboard/individual/tournaments';
  }, [isIndividualRoute, managementScope, managerScopeId, routeClubId, routeCommunityId, routeOrganizationId, routeTournamentId]);
  const communityScope = routeCommunityId || user?.community;
  const communityId = useMemo(() => getCommunityId(communityScope), [communityScope]);

  useEffect(() => {
    const loadTournaments = async () => {
      if (!token || !hasTournamentAdminAccess) return;

      setIsLoading(true);
      setLoadError('');

      try {
        if (isIndividualRoute && routeTournamentId) {
          const detailResponse = await fetchTournamentById(routeTournamentId, token);
          const singleTournament = detailResponse?.tournament || detailResponse || null;
          setTournaments(singleTournament ? [singleTournament] : []);
        } else {
          const organizationScope = routeOrganizationId || user?.organization;
          const response = await fetchTournaments(
            token,
            organizationScope,
            communityId ? { communityId } : {}
          );
          const tournamentsData = response.tournaments || [];

          setTournaments(tournamentsData);

          if (
            routeTournamentId &&
            !tournamentsData.some((tournament) => getTournamentId(tournament) === routeTournamentId)
          ) {
            const detailResponse = await fetchTournamentById(
              routeTournamentId,
              token,
              routeOrganizationId || user?.organization,
              communityId ? { communityId } : {}
            );
            if (detailResponse?.tournament) {
              setTournaments((prev) => {
                const exists = prev.some((item) => getTournamentId(item) === routeTournamentId);
                return exists ? prev : [detailResponse.tournament, ...prev];
              });
            }
          }
        }
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [
    communityId,
    hasTournamentAdminAccess,
    routeOrganizationId,
    routeTournamentId,
    token,
    user?.organization,
    isIndividualRoute,
    ownerType,
  ]);

  const assignedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments)) return [];

    if (isSuperAdmin) return tournaments;

    if (!managerScopeId && !normalizedUserId) return tournaments;

    return tournaments.filter((tournament) => {
      const adminMatch = (tournament.admins || []).some(
        (admin) => normalizeId(admin.id || admin._id) === normalizedUserId,
      );

      const managerMatch = ['registrationManager', 'auctionManager', 'matchManager'].some((role) =>
        normalizeId(tournament[role]?.id || tournament[role]?._id) === managerScopeId,
      );

      return adminMatch || managerMatch;
    });
  }, [isSuperAdmin, managerScopeId, normalizedUserId, tournaments]);

  const isRegistrationManagerForTournament = useCallback(
    (tournament) => {
      const registrationManagerId = normalizeId(
        tournament?.registrationManager?.id || tournament?.registrationManager?._id,
      );

      return Boolean(managerScopeId && registrationManagerId && registrationManagerId === managerScopeId);
    },
    [managerScopeId],
  );

  const tournamentAdminView = (isTournamentAdmin || isOrganizationAdmin) && !isSuperAdmin;

  const detailTournament = useMemo(() => {
    if (!routeTournamentId) return null;

    return tournaments.find((tournament) => getTournamentId(tournament) === routeTournamentId) || null;
  }, [routeTournamentId, tournaments]);

  const tournamentsForPlayers = useMemo(() => {
    if (routeTournamentId) {
      return detailTournament ? [detailTournament] : [];
    }

    if (!tournamentAdminView) return assignedTournaments;

    return assignedTournaments.filter(isRegistrationManagerForTournament);
  }, [
    assignedTournaments,
    detailTournament,
    isRegistrationManagerForTournament,
    routeTournamentId,
    tournamentAdminView,
  ]);

  const getProfileImage = (player) =>
    sanitizeProfilePhoto(
      player.profileImage ||
        player.profilePhoto ||
        player.profilePhotoUrl ||
        player.profilePhotoURL ||
        player.media?.profilePhoto ||
        player.media?.profilePhotoUrl ||
        player.user?.profilePhoto ||
        player.user?.profileImage ||
        ''
    );

  const getEmail = (player) =>
    player.email ||
    player.user?.email ||
    player.personal?.email ||
    player.contact?.email ||
    player.contactDetails?.email ||
    '';

  const normalizePlayersForDisplay = (players) =>
    (players || [])
      .map((player) => {
        if (!player) return null;

        if (typeof player === 'string') {
          return { id: player, name: 'Unknown player', role: '' };
        }

        const approvalStatus = player?.user?.approvalStatus || player?.approvalStatus;
        if (!isApprovedPlayerStatus(approvalStatus)) {
          return null;
        }

        const name =
          player.fullName ||
          player.name ||
          player.personal?.fullName ||
          player.user?.name ||
          player.user?.email ||
          'Unknown player';

        const email = getEmail(player);

        const profileImage = getProfileImage(player);

        return {
          id: player.id || player._id || player._id?.toString?.() || player.toString?.(),
          name,
          role: player.primaryRole,
          email,
          profileImage,
        };
      })
      .filter((player) => player && (player.id || player.name));

  const replaceTournamentInState = (updatedTournament) => {
    if (!updatedTournament) return;

    setTournaments((prev) =>
      prev.map((tournament) => {
        const tournamentId = getTournamentId(tournament);
        const updatedId = getTournamentId(updatedTournament);
        return tournamentId === updatedId ? updatedTournament : tournament;
      }),
    );
  };

  const isRemovingPlayer = (tournamentId, playerId) => {
    const normalizedTournamentId = normalizeId(tournamentId);
    const normalizedPlayerId = normalizeId(playerId);

    return Boolean(removingPlayers[normalizedTournamentId]?.[normalizedPlayerId]);
  };

  const renderPlayerList = (players, emptyText, options = {}) => {
    const normalizedPlayers = normalizePlayersForDisplay(players);
    const { onRemove, tournamentId } = options;

    if (normalizedPlayers.length === 0) {
      return <span className="text-muted small">{emptyText}</span>;
    }

    return (
      <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
        {normalizedPlayers.map((player) => (
          <div key={player.id || player.name} className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle bg-secondary-subtle d-inline-flex align-items-center justify-content-center"
              style={{ width: '36px', height: '36px' }}
            >
              {player.profileImage ? (
                <img
                  src={player.profileImage}
                  alt={player.name}
                  className="img-fluid rounded-circle"
                  style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                />
              ) : (
                <span className="small fw-semibold text-secondary">{(player.name || '?').charAt(0)}</span>
              )}
            </div>
            <div className="d-grid">
              <p className="mb-0 fw-semibold">{player.name}</p>
              <p className="mb-0 text-muted small">{player.email || 'No email provided'}</p>
              <p className="mb-0 text-muted small">
                Category: {getPlayerCategoryLabel(tournamentId, player.id)}
              </p>
            </div>
            {player.role && <span className="badge text-bg-light ms-auto">{player.role}</span>}
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-auto"
                onClick={() => onRemove(player.id)}
                disabled={isRemovingPlayer(tournamentId, player.id)}
              >
                {isRemovingPlayer(tournamentId, player.id) ? 'Removing...' : 'Remove'}
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getSelectedPlayerCategories = (tournamentId, selectedIds, availablePlayers) => {
    if (!selectedIds?.length) return [];

    const categoriesForTournament = playerCategories[tournamentId] || [];
    const assignments = playerCategoryAssignments[tournamentId] || {};

    const normalizedPlayers = normalizePlayersForDisplay(availablePlayers);
    const playerLookup = normalizedPlayers.reduce((acc, player) => {
      if (!player?.id) return acc;

      acc[normalizeId(player.id)] = player;
      return acc;
    }, {});

    const buckets = new Map();

    selectedIds.forEach((id) => {
      const normalizedId = normalizeId(id);
      const player = playerLookup[normalizedId] || { id: normalizedId, name: 'Unknown player', role: '' };
      const categoryId = assignments[normalizedId] || '';
      const matchedCategory = categoriesForTournament.find(
        (category) => normalizeId(category.id) === normalizeId(categoryId),
      );
      const categoryLabel = matchedCategory?.name || 'Uncategorized';
      const bucketKey = categoryId || 'uncategorized';
      const existing = buckets.get(bucketKey) || { name: categoryLabel, role: categoryLabel, players: [] };

      buckets.set(bucketKey, {
        ...existing,
        name: categoryLabel,
        role: categoryLabel,
        players: [...existing.players, player],
      });
    });

    return Array.from(buckets.values()).map((bucket) => ({
      ...bucket,
      count: bucket.players.length,
    }));
  };

  const getPlayerCategoryLabel = (tournamentId, playerId) => {
    if (!tournamentId || !playerId) return 'Uncategorized';

    const categoriesForTournament = playerCategories[tournamentId] || [];
    const assignments = playerCategoryAssignments[tournamentId] || {};
    const assignedCategoryId = assignments[normalizeId(playerId)];

    if (!assignedCategoryId) return 'Uncategorized';

    const matchedCategory = categoriesForTournament.find(
      (category) => normalizeId(category.id) === normalizeId(assignedCategoryId),
    );

    return matchedCategory?.name || 'Uncategorized';
  };

  const handlePlayerSelectionChange = (tournamentId, selectedOptions) => {
    const values = Array.from(selectedOptions || []).map((option) => option.value);
    setSelectedPlayers((prev) => ({ ...prev, [tournamentId]: values }));
  };

  const handleRemoveSelectedPlayer = (tournamentId, playerId) => {
    setSelectedPlayers((prev) => {
      const existing = prev[tournamentId] || [];
      const updated = existing.filter((id) => normalizeId(id) !== normalizeId(playerId));
      return { ...prev, [tournamentId]: updated };
    });
  };

  const handleRemoveTournamentPlayer = async (tournamentId, playerId) => {
    if (!tournamentId || !playerId) return;

    const normalizedTournamentId = normalizeId(tournamentId);
    const normalizedPlayerId = normalizeId(playerId);

    setRemovingPlayers((prev) => ({
      ...prev,
      [normalizedTournamentId]: {
        ...(prev[normalizedTournamentId] || {}),
        [normalizedPlayerId]: true,
      },
    }));
    setActionStatus((prev) => ({
      ...prev,
      [normalizedTournamentId]: { tone: 'info', message: 'Removing player from tournament...' },
    }));

    try {
      const response = await removeTournamentPlayer(normalizedTournamentId, normalizedPlayerId, token);
      if (response?.tournament) {
        replaceTournamentInState(response.tournament);
      }

      setActionStatus((prev) => ({
        ...prev,
        [normalizedTournamentId]: {
          tone: 'success',
          message: response?.message || 'Player removed from the tournament.',
        },
      }));

      await loadEligiblePlayers(normalizedTournamentId);
    } catch (error) {
      console.error('[PLAYER MANAGEMENT API ERROR]', {
        apiUrl: `/api/tournaments/${tournamentId}/eligible-players`,
        status: error?.status,
        message: error?.message,
        activeContextHeaders: {
          'x-active-context-type': isIndividualRoute ? 'individual' : ownerType,
          'x-active-entity-id': isIndividualRoute ? '' : routeOrganizationId || routeCommunityId || routeClubId || '',
        },
      });
      setActionStatus((prev) => ({
        ...prev,
        [normalizedTournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to remove this player right now.',
        },
      }));
    } finally {
      setRemovingPlayers((prev) => {
        const existingTournamentRemovals = { ...(prev[normalizedTournamentId] || {}) };
        delete existingTournamentRemovals[normalizedPlayerId];

        const updated = { ...prev, [normalizedTournamentId]: existingTournamentRemovals };

        if (Object.keys(existingTournamentRemovals).length === 0) {
          delete updated[normalizedTournamentId];
        }

        return updated;
      });
    }
  };

  const syncCategoryState = useCallback((tournamentId, categories = []) => {
    const normalizedCategories = categories || [];
    setPlayerCategories((prev) => ({ ...prev, [tournamentId]: normalizedCategories }));
    setCategoryEdits((prev) => ({
      ...prev,
      [tournamentId]: normalizedCategories.reduce((acc, category) => {
        const categoryId = normalizeId(category.id || category._id);
        if (categoryId) {
          acc[categoryId] = category.name;
        }
        return acc;
      }, {}),
    }));

    const assignments = normalizedCategories.reduce((acc, category) => {
      const categoryId = normalizeId(category.id || category._id);
      (category.players || []).forEach((player) => {
        const playerId = normalizeId(player.id || player._id || player);
        if (playerId) {
          acc[playerId] = categoryId;
        }
      });
      return acc;
    }, {});

    setPlayerCategoryAssignments((prev) => ({ ...prev, [tournamentId]: assignments }));
  }, []);

  const loadPlayerCategories = useCallback(
    async (tournamentId) => {
      if (!tournamentId || !token) return;

      setLoadingCategories((prev) => ({ ...prev, [tournamentId]: true }));

      try {
        const response = await fetchPlayerCategories(tournamentId, token);
        const categories = response.categories || [];
        syncCategoryState(tournamentId, categories);
        setCategoryDrafts((prev) => ({ ...prev, [tournamentId]: prev[tournamentId] || '' }));
      } catch (error) {
        setActionStatus((prev) => ({
          ...prev,
          [tournamentId]: {
            tone: 'danger',
            message: error.message || 'Unable to load categories right now.',
          },
        }));
      } finally {
        setLoadingCategories((prev) => ({ ...prev, [tournamentId]: false }));
      }
    },
    [syncCategoryState, token],
  );

  const loadEligiblePlayers = useCallback(async (tournamentId) => {
    if (!tournamentId || !token) return;

    setLoadingEligible((prev) => ({ ...prev, [tournamentId]: true }));
    setActionStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Loading eligible players...' },
    }));

    try {
      const response = await fetchEligibleTournamentPlayers(tournamentId, token, {
        organizationId: isIndividualRoute ? '' : (routeOrganizationId || user?.organization),
        communityId: isIndividualRoute ? '' : (routeCommunityId || user?.community),
        clubId: isIndividualRoute ? '' : (routeClubId || user?.club),
      });
      const players = response.players || response.eligiblePlayers || [];

      setEligiblePlayers((prev) => ({ ...prev, [tournamentId]: players }));
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'success', message: 'Eligible players refreshed.' },
      }));
    } catch (error) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to load eligible players right now.',
        },
      }));
    } finally {
      setLoadingEligible((prev) => ({ ...prev, [tournamentId]: false }));
    }
  }, [routeClubId, routeCommunityId, routeOrganizationId, token, user?.club, user?.community, user?.organization]);

  const handleCreateCategory = async (tournamentId) => {
    const name = (categoryDrafts[tournamentId] || '').trim();

    if (!name) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: 'Enter a name before creating a category.',
        },
      }));
      return;
    }

    setCategorySaving((prev) => ({ ...prev, [tournamentId]: true }));
    setActionStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Saving category...' },
    }));

    try {
      const response = await createPlayerCategory(tournamentId, name, token);
      const categories = response.categories || [];
      syncCategoryState(tournamentId, categories);
      setCategoryDrafts((prev) => ({ ...prev, [tournamentId]: '' }));
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'success',
          message: response?.message || 'Category created successfully.',
        },
      }));
    } catch (error) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to create category right now.',
        },
      }));
    } finally {
      setCategorySaving((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleRenameCategory = async (tournamentId, categoryId) => {
    const updatedName = (categoryEdits[tournamentId]?.[categoryId] || '').trim();

    if (!updatedName) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: 'Category name cannot be empty.',
        },
      }));
      return;
    }

    setCategorySaving((prev) => ({ ...prev, [tournamentId]: true }));

    try {
      const response = await updatePlayerCategory(tournamentId, categoryId, updatedName, token);
      syncCategoryState(tournamentId, response.categories || []);
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'success',
          message: response?.message || 'Category updated.',
        },
      }));
    } catch (error) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to update category right now.',
        },
      }));
    } finally {
      setCategorySaving((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleDeleteCategory = async (tournamentId, categoryId) => {
    setCategorySaving((prev) => ({ ...prev, [tournamentId]: true }));

    try {
      const response = await deletePlayerCategory(tournamentId, categoryId, token);
      syncCategoryState(tournamentId, response.categories || []);
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'success',
          message: response?.message || 'Category removed.',
        },
      }));
    } catch (error) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to delete category right now.',
        },
      }));
    } finally {
      setCategorySaving((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleAssignCategory = async (tournamentId, playerId, categoryId) => {
    if (!playerId) return;

    const normalizedTournamentId = normalizeId(tournamentId);
    const normalizedPlayerId = normalizeId(playerId);

    const previousAssignment =
      playerCategoryAssignments[normalizedTournamentId]?.[normalizedPlayerId] ?? '';

    setPlayerCategoryAssignments((prev) => ({
      ...prev,
      [normalizedTournamentId]: {
        ...(prev[normalizedTournamentId] || {}),
        [normalizedPlayerId]: categoryId || '',
      },
    }));

    setCategorySaving((prev) => ({ ...prev, [normalizedTournamentId]: true }));

    try {
      const response = await assignPlayersToCategory(
        normalizedTournamentId,
        [normalizedPlayerId],
        categoryId || null,
        token,
      );
      syncCategoryState(normalizedTournamentId, response.categories || []);
      if (response.tournament) {
        replaceTournamentInState(response.tournament);
      }
      setActionStatus((prev) => ({
        ...prev,
        [normalizedTournamentId]: {
          tone: 'success',
          message: response?.message || 'Player categorization updated.',
        },
      }));
    } catch (error) {
      setPlayerCategoryAssignments((prev) => ({
        ...prev,
        [normalizedTournamentId]: {
          ...(prev[normalizedTournamentId] || {}),
          [normalizedPlayerId]: previousAssignment,
        },
      }));
      setActionStatus((prev) => ({
        ...prev,
        [normalizedTournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to update player category right now.',
        },
      }));
    } finally {
      setCategorySaving((prev) => ({ ...prev, [normalizedTournamentId]: false }));
    }
  };

  const persistSelectedCategories = useCallback(
    async (tournamentId, playerIds) => {
      const normalizedTournamentId = normalizeId(tournamentId);
      const assignments = playerCategoryAssignments[normalizedTournamentId] || {};
      const categories = playerCategories[normalizedTournamentId] || [];
      const validCategoryIds = new Set(categories.map((category) => normalizeId(category.id)));
      const uniquePlayerIds = Array.from(new Set((playerIds || []).map((id) => normalizeId(id)).filter(Boolean)));

      if (uniquePlayerIds.length === 0) return;

      const buckets = uniquePlayerIds.reduce((acc, playerId) => {
        const categoryId = assignments[playerId] || '';
        const normalizedCategoryId = validCategoryIds.has(normalizeId(categoryId)) ? normalizeId(categoryId) : '';
        const existing = acc[normalizedCategoryId] || [];
        return { ...acc, [normalizedCategoryId]: [...existing, playerId] };
      }, {});

      for (const [categoryId, players] of Object.entries(buckets)) {
        const response = await assignPlayersToCategory(
          normalizedTournamentId,
          players,
          categoryId || null,
          token,
        );

        syncCategoryState(normalizeId(normalizedTournamentId), response.categories || []);
        if (response.tournament) {
          replaceTournamentInState(response.tournament);
        }
      }
    },
    [playerCategoryAssignments, playerCategories, replaceTournamentInState, syncCategoryState, token],
  );

  useEffect(() => {
    tournamentsForPlayers.forEach((tournament) => {
      const tournamentId = getTournamentId(tournament);

      if (
        !tournamentId ||
        eligiblePlayers[tournamentId] ||
        loadingEligible[tournamentId]
      ) {
        return;
      }

      loadEligiblePlayers(tournamentId);
    });
  }, [eligiblePlayers, loadEligiblePlayers, loadingEligible, tournamentsForPlayers]);

  useEffect(() => {
    tournamentsForPlayers.forEach((tournament) => {
      const tournamentId = getTournamentId(tournament);

      if (!tournamentId || playerCategories[tournamentId] || loadingCategories[tournamentId]) {
        return;
      }

      loadPlayerCategories(tournamentId);
    });
  }, [loadPlayerCategories, loadingCategories, playerCategories, tournamentsForPlayers]);

  useEffect(() => {
    const loadTournamentJoinLinks = async () => {
      if (!token) return;

      await Promise.all(
        tournamentsForPlayers.map(async (tournament) => {
          const tournamentId = getTournamentId(tournament);
          if (!tournamentId || tournamentJoinLinks[tournamentId]) {
            return;
          }

          if (joinLinkAutoGeneratedRef.current[tournamentId]) {
            return;
          }

          joinLinkAutoGeneratedRef.current[tournamentId] = true;

          try {
            const data = await getTournamentJoinLink(tournamentId, token);
            setTournamentJoinLinks((prev) => ({ ...prev, [tournamentId]: data }));
            return;
          } catch (error) {
            const status = logApiError(`/api/tournaments/${tournamentId}/join-link`, error, { action: 'load-join-link' });
            if (status !== 404) {
              setTournamentJoinLinkStatus((prev) => ({
                ...prev,
                [tournamentId]: { tone: 'danger', message: error.message || 'Unable to load join link.' },
              }));
              return;
            }
          }

          await handleGenerateTournamentJoinLink(tournamentId, { silentStatus: true });
        }),
      );
    };

    loadTournamentJoinLinks();
  }, [token, tournamentsForPlayers]);

  const handleGenerateTournamentJoinLink = async (tournamentId, options = {}) => {
    if (!tournamentId || !token) return;
    const { silentStatus = false, regenerate = false } = options;

    setGeneratingJoinLink((prev) => ({ ...prev, [tournamentId]: true }));
    if (!silentStatus) {
      setTournamentJoinLinkStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'info', message: 'Generating join link...' } }));
    }

    try {
      const response = await createTournamentJoinLink(tournamentId, token, regenerate ? { regenerate: true } : {});
      const rawGeneratedLink = response?.link || response?.joinUrl || response?.url || response?.joinLink || '';

      const generatedLink = rawGeneratedLink
        ? /^https?:\/\//i.test(rawGeneratedLink)
          ? rawGeneratedLink
          : `${window.location.origin}${rawGeneratedLink.startsWith('/') ? '' : '/'}${rawGeneratedLink}`
        : '';

      if (!generatedLink) {
        throw new Error('Join link was not returned by the server.');
      }

      setTournamentJoinLinks((prev) => ({
        ...prev,
        [tournamentId]: {
          ...response,
          link: generatedLink,
          joinUrl: response?.joinUrl || generatedLink,
        },
      }));
      setTournamentJoinLinkStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'success',
          message: response?.message || 'Tournament join link generated successfully.',
        },
      }));
    } catch (error) {
      logApiError(`/api/tournaments/${tournamentId}/join-link`, error, { action: 'create-join-link' });
      setTournamentJoinLinkStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to generate join link right now.',
        },
      }));
    } finally {
      setGeneratingJoinLink((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };


  const buildTeamRegistrationLink = (invite) => {
    const raw = invite?.url || invite?.link || '';
    if (!raw) return '';
    return /^https?:\/\//i.test(raw) ? raw : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
  };

  const handleGenerateTeamRegistrationInvite = async (tournamentId, regenerate = false) => {
    if (!tournamentId || !token) return;
    setTeamRegistrationLoading((prev) => ({ ...prev, [tournamentId]: true }));
    setTeamRegistrationStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'info', message: regenerate ? 'Regenerating team link...' : 'Generating team link...' } }));
    try {
      const response = regenerate
        ? await regenerateTeamRegistrationInvite(tournamentId, token)
        : await createTeamRegistrationInvite(tournamentId, token);
      setTeamRegistrationInvites((prev) => ({ ...prev, [tournamentId]: response.invite }));
      setTeamRegistrationStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'success', message: 'Team registration link ready.' } }));
    } catch (error) {
      setTeamRegistrationStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'danger', message: error.message || 'Unable to prepare team registration link.' } }));
    } finally {
      setTeamRegistrationLoading((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleCopyTeamRegistrationInvite = async (tournamentId) => {
    const link = buildTeamRegistrationLink(teamRegistrationInvites[tournamentId]);
    if (!link) return;
    await copyTextToClipboard(link);
    setTeamRegistrationStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'success', message: 'Team registration link copied.' } }));
  };

  const handleDisableTeamRegistrationInvite = async (tournamentId) => {
    if (!window.confirm('Disable this team registration link?')) return;
    await disableTeamRegistrationInvite(tournamentId, token);
    setTeamRegistrationInvites((prev) => ({ ...prev, [tournamentId]: null }));
    setTeamRegistrationStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'success', message: 'Team registration link disabled.' } }));
  };

  const handleCopyTournamentJoinLink = async (tournamentId) => {
    const data = tournamentJoinLinks[tournamentId];
    const joinLink = data?.link || (data?.joinUrl ? `${window.location.origin}${data.joinUrl.startsWith('/') ? '' : '/'}${data.joinUrl}` : '');
    if (!joinLink) return;

    try {
      await copyTextToClipboard(joinLink);
      setTournamentJoinLinkStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'success', message: 'Join link copied.' } }));
    } catch (_error) {
      try {
        await navigator.clipboard.writeText(joinLink);
        setTournamentJoinLinkStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'success', message: 'Join link copied.' } }));
      } catch (error) {
        setTournamentJoinLinkStatus((prev) => ({ ...prev, [tournamentId]: { tone: 'danger', message: error.message || 'Unable to copy join link.' } }));
      }
    }
  };

  const removeTournamentFromState = (tournamentId) => {
    setTournaments((prev) => prev.filter((tournament) => getTournamentId(tournament) !== tournamentId));
    setEligiblePlayers((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
    setSelectedPlayers((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
    setPlayerCategories((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
    setPlayerCategoryAssignments((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
    setCategoryDrafts((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
    setCategoryEdits((prev) => {
      const updated = { ...prev };
      delete updated[tournamentId];
      return updated;
    });
  };

  const handlePlayerAction = async (tournamentId, action) => {
    if (!tournamentId || !action) return;

    const selected = selectedPlayers[tournamentId] || [];

    if (selected.length === 0) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'danger', message: 'Select at least one player to continue.' },
      }));
      return;
    }

    setSavingTournament((prev) => ({ ...prev, [tournamentId]: true }));
    setActionStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Saving player updates...' },
    }));

    try {
      await persistSelectedCategories(tournamentId, selected);
      const response =
        action === 'add'
          ? await addTournamentPlayers(tournamentId, selected, token)
          : await inviteTournamentPlayers(tournamentId, selected, token);

      const updatedTournament = response.tournament;

      if (updatedTournament) {
        replaceTournamentInState(updatedTournament);
      }

      setSelectedPlayers((prev) => ({ ...prev, [tournamentId]: [] }));
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'success',
          message:
            response.message ||
            (action === 'add'
              ? 'Players added to the tournament.'
              : 'Invitations sent to selected players.'),
        },
      }));
    } catch (error) {
      setActionStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to update players right now.',
        },
      }));
    } finally {
      setSavingTournament((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleArchiveTournament = async (tournamentId, tournamentName, isActive) => {
    if (!tournamentId) return;

    const confirmed = window.confirm(
      isActive
        ? `${tournamentName || 'This tournament'} will be deleted. Assigned admins and players will be retained. Continue?`
        : `${tournamentName || 'This tournament'} is archived and will be permanently removed. Continue?`,
    );

    if (!confirmed) return;

    setArchivingTournament((prev) => ({ ...prev, [tournamentId]: true }));
    setArchiveStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Deleting tournament...' },
    }));

    try {
      const response = await deleteTournament(tournamentId, token);
      const updatedTournament = response.tournament;
      if (updatedTournament) {
        replaceTournamentInState(updatedTournament);
      } else {
        removeTournamentFromState(tournamentId);
      }

      setArchiveStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'success', message: response.message || 'Tournament deleted.' },
      }));
    } catch (error) {
      setArchiveStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'danger', message: error.message || 'Unable to delete tournament.' },
      }));
    } finally {
      setArchivingTournament((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };

  const handleActivateTournament = async (tournamentId, tournamentName) => {
    if (!tournamentId) return;

    const confirmed = window.confirm(
      `${tournamentName || 'This tournament'} will be moved back to active status. Continue?`,
    );

    if (!confirmed) return;

    setReactivatingTournament((prev) => ({ ...prev, [tournamentId]: true }));
    setArchiveStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Activating tournament...' },
    }));

    try {
      const response = await activateTournament(tournamentId, token);
      const updatedTournament = response.tournament;

      if (updatedTournament) {
        replaceTournamentInState(updatedTournament);
      }

      setArchiveStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'success', message: response.message || 'Tournament activated.' },
      }));
    } catch (error) {
      setArchiveStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'danger', message: error.message || 'Unable to activate tournament.' },
      }));
    } finally {
      setReactivatingTournament((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };


  const canManageTournament = useMemo(() => {
    if (!detailTournament) return hasTournamentAdminAccess;

    const tournament = detailTournament;
    const currentUserId = normalizeId(user?._id || user?.id);
    const isIndividualOwner =
      tournament?.ownerType === 'individual' &&
      normalizeId(tournament?.ownerUser) === currentUserId;
    const isCreator = normalizeId(tournament?.createdBy) === currentUserId;
    const isAssignedPlayerManager =
      normalizeId(tournament?.registrationManager) === currentUserId ||
      normalizeId(tournament?.playerManager) === currentUserId ||
      (tournament?.playerManagers || []).some((id) => normalizeId(id) === currentUserId);

    const existingCanManageLogic = hasTournamentAdminAccess;
    const canManage =
      isIndividualOwner ||
      isCreator ||
      isAssignedPlayerManager ||
      existingCanManageLogic;

    return {
      canManage,
      currentUserId,
      tournament,
      isOwner: isIndividualOwner,
      isCreator,
      isAssignedPlayerManager,
    };
  }, [detailTournament, hasTournamentAdminAccess, user?._id, user?.id]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return <LoadingState message="Loading player management..." />;
  }

  if (!canManageTournament?.canManage && detailTournament) {
    return <div className="alert alert-danger mb-0">You are not authorized to perform this action.</div>;
  }

  const detailNotFound =
    Boolean(routeTournamentId) && !isLoading && tournaments.length > 0 && tournamentsForPlayers.length === 0;

  return (
    <div className="py-4 d-grid gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Player management</p>
          <h1 className="h4 mb-2">Add or invite players from your organization</h1>
          <p className="text-muted mb-0">
            Manage tournament rosters, send invitations, and keep your player lists up to date.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(workplacePath)}>
            Back to Manager workspace
          </button>
        </div>
      </div>

      {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
      {detailNotFound && (
        <div className="alert alert-warning mb-0">Unable to find that tournament. Try reloading the page.</div>
      )}

      {!isLoading && tournamentsForPlayers.length === 0 && (
        <div className="alert alert-info mb-0">No tournaments available to manage players.</div>
      )}

      {tournamentsForPlayers.map((tournament) => {
        const tournamentId = getTournamentId(tournament);
        const status = actionStatus[tournamentId];
        const selected = selectedPlayers[tournamentId] || [];
        const availablePlayers = eligiblePlayers[tournamentId] || [];
        const rosteredPlayers = normalizePlayersForDisplay(tournament.players);
        const invitedPlayers = normalizePlayersForDisplay(tournament.invitedPlayers);
        const eligiblePlayersForSelect = normalizePlayersForDisplay(availablePlayers).filter(
          (player) => player.id,
        );
        const selectedPlayerDetails = selected
          .map((playerId) =>
            eligiblePlayersForSelect.find((player) => normalizeId(player.id) === normalizeId(playerId)),
          )
          .filter(Boolean);
        const selectedCategories = getSelectedPlayerCategories(tournamentId, selected, availablePlayers);
        const categoriesForTournament = playerCategories[tournamentId] || [];
        const categoryDraft = categoryDrafts[tournamentId] || '';
        const categoryEditValues = categoryEdits[tournamentId] || {};
        const playerAssignments = playerCategoryAssignments[tournamentId] || {};
        const isSavingCategory = Boolean(categorySaving[tournamentId]);
        const isLoadingCategories = Boolean(loadingCategories[tournamentId]);
        const isSaving = Boolean(savingTournament[tournamentId]);
        const isLoadingPlayers = Boolean(loadingEligible[tournamentId]);
        const isActive = tournament.isActive !== false;
        const archiveMessage = archiveStatus[tournamentId];
        const isArchiving = Boolean(archivingTournament[tournamentId]);
        const isReactivating = Boolean(reactivatingTournament[tournamentId]);
        const activeManagementTab = managementTabSelections[tournamentId] || 'players';

        return (
          <div key={tournamentId} className="card border-0 shadow-sm">
            <div className="card-body d-grid gap-3">
              <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                  <h2 className="h5 mb-1">{tournament.name || 'Untitled tournament'}</h2>
                  <p className="mb-0 text-muted small">Rostered: {rosteredPlayers.length} • Invited: {invitedPlayers.length}</p>
                </div>
                <span
                  className={`badge ${
                    isActive
                      ? 'bg-success-subtle text-success-emphasis'
                      : 'bg-secondary-subtle text-secondary-emphasis'
                  } align-self-start`}
                >
                  {isActive ? 'Active' : 'Archived'}
                </span>
              </div>

              <div className="border rounded p-3 bg-light-subtle d-grid gap-2">
                <div>
                  <p className="small fw-semibold mb-1">Tournament Join Link</p>
                  <p className="text-muted small mb-0">Share this link so players can verify OTP and join this tournament.</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => handleGenerateTournamentJoinLink(tournamentId, { regenerate: true })}
                    disabled={Boolean(generatingJoinLink[tournamentId])}
                  >
                    {generatingJoinLink[tournamentId] ? 'Generating...' : 'Generate Link'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleCopyTournamentJoinLink(tournamentId)}
                    disabled={!(tournamentJoinLinks[tournamentId]?.link || tournamentJoinLinks[tournamentId]?.joinUrl)}
                  >
                    Copy Link
                  </button>
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  readOnly
                  value={tournamentJoinLinks[tournamentId]?.link || tournamentJoinLinks[tournamentId]?.joinUrl || ''}
                  placeholder="Generate a join link to share with players"
                />
                {tournamentJoinLinkStatus[tournamentId]?.message && (
                  <div className={`alert alert-${
                    tournamentJoinLinkStatus[tournamentId].tone === 'success'
                      ? 'success'
                      : tournamentJoinLinkStatus[tournamentId].tone === 'danger'
                        ? 'danger'
                        : 'info'
                  } py-2 mb-0`}>
                    {tournamentJoinLinkStatus[tournamentId].message}
                  </div>
                )}
              </div>


              <div className="border rounded p-3 bg-light-subtle d-grid gap-2">
                <div>
                  <p className="small fw-semibold mb-1">Join Tournament as a Team</p>
                  <p className="text-muted small mb-0">Share this isolated link so a team contact can draft a roster from registered players.</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => handleGenerateTeamRegistrationInvite(tournamentId)} disabled={Boolean(teamRegistrationLoading[tournamentId])}>{teamRegistrationLoading[tournamentId] ? 'Working...' : 'Generate Team Registration Link'}</button>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleCopyTeamRegistrationInvite(tournamentId)} disabled={!buildTeamRegistrationLink(teamRegistrationInvites[tournamentId])}>Share/Copy Link</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleGenerateTeamRegistrationInvite(tournamentId, true)}>Regenerate Link</button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDisableTeamRegistrationInvite(tournamentId)} disabled={!teamRegistrationInvites[tournamentId]}>Disable Link</button>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => navigate(`/player-manager/tournaments/${tournamentId}/team-registrations`)}>View Team Registrations</button>
                </div>
                <input type="text" className="form-control form-control-sm" readOnly value={buildTeamRegistrationLink(teamRegistrationInvites[tournamentId])} placeholder="Generate a team registration link" />
                {teamRegistrationStatus[tournamentId]?.message && <div className={`alert alert-${teamRegistrationStatus[tournamentId].tone === 'danger' ? 'danger' : teamRegistrationStatus[tournamentId].tone === 'success' ? 'success' : 'info'} py-2 mb-0`}>{teamRegistrationStatus[tournamentId].message}</div>}
              </div>

              <div className="d-flex flex-wrap gap-2 border-bottom pb-2">
                {['players', 'categories'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`btn btn-sm ${
                      activeManagementTab === tab ? 'btn-primary' : 'btn-outline-primary'
                    }`}
                    onClick={() =>
                      setManagementTabSelections((prev) => ({
                        ...prev,
                        [tournamentId]: tab,
                      }))
                    }
                  >
                    {tab === 'players' ? 'Players' : 'Category'}
                  </button>
                ))}
              </div>

              {activeManagementTab === 'players' && (
                <div className="d-grid gap-3">
                  <div className="d-grid gap-2">
                    <div>
                      <p className="small fw-semibold mb-1">Players in tournament</p>
                      {renderPlayerList(rosteredPlayers, 'No players added yet.', {
                        tournamentId,
                        onRemove: isActive
                          ? (playerId) => handleRemoveTournamentPlayer(tournamentId, playerId)
                          : null,
                      })}
                    </div>
                    <div>
                      <p className="small fw-semibold mb-1">Invited players</p>
                      {renderPlayerList(invitedPlayers, 'No pending invites.', {
                        tournamentId,
                        onRemove: isActive
                          ? (playerId) => handleRemoveTournamentPlayer(tournamentId, playerId)
                          : null,
                      })}
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    <label className="form-label small mb-1" htmlFor={`playerSelect-${tournamentId}`}>
                      Select players from your organization
                    </label>
                    <select
                      id={`playerSelect-${tournamentId}`}
                      className="form-select"
                      multiple
                      value={selected}
                      onChange={(event) => handlePlayerSelectionChange(tournamentId, event.target.selectedOptions)}
                      disabled={isLoadingPlayers || !isActive}
                    >
                      {isLoadingPlayers && <option>Loading available players...</option>}
                      {!isLoadingPlayers && availablePlayers.length === 0 && (
                        <option>No eligible players available</option>
                      )}
                    {!isLoadingPlayers &&
                      eligiblePlayersForSelect.map((player) => (
                        <option key={player.id || player.name} value={player.id}>
                          {player.name}
                          {player.role ? ` — ${player.role}` : ''}
                        </option>
                      ))}
                  </select>
                  <div className="d-grid gap-2">
                    <p className="small fw-semibold mb-1">Selected players</p>
                  {selectedPlayerDetails.length === 0 ? (
                    <span className="text-muted small">Select players above to add or invite.</span>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {selectedPlayerDetails.map((player) => (
                        <span key={player.id} className="badge bg-primary-subtle text-primary-emphasis">
                          <span className="fw-semibold">{player.name}</span>
                          <span className="text-muted ms-1">• {getPlayerCategoryLabel(tournamentId, player.id)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                </div>

                  <div className="d-grid gap-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <p className="small fw-semibold mb-1 mb-0">Assign categories</p>
                      {isLoadingCategories && <span className="text-muted small">Loading categories...</span>}
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none"
                        onClick={() => loadPlayerCategories(tournamentId)}
                        disabled={isLoadingCategories}
                      >
                        Refresh categories
                      </button>
                    </div>
                    <div className="d-grid gap-2">
                      {selectedPlayerDetails.length === 0 ? (
                        <span className="text-muted small">
                          Select players above to categorize them before adding or inviting them.
                        </span>
                      ) : (
                        <div className="d-grid gap-2">
                          <span className="text-muted small">
                            Use the category dropdown on each player to change their category before adding or
                            inviting them.
                          </span>
                          {selectedPlayerDetails.map((player) => (
                            <div key={player.id} className="d-flex align-items-center gap-3 border rounded p-2">
                              <div
                                className="rounded-circle bg-secondary-subtle d-inline-flex align-items-center justify-content-center"
                                style={{ width: '44px', height: '44px' }}
                              >
                                {player.profileImage ? (
                                  <img
                                    src={player.profileImage}
                                    alt={player.name}
                                    className="img-fluid rounded-circle"
                                    style={{ width: '44px', height: '44px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <span className="small fw-semibold text-secondary">
                                    {(player.name || '?').charAt(0)}
                                  </span>
                                )}
                              </div>
                              <div className="d-grid">
                                <p className="mb-0 fw-semibold">{player.name}</p>
                                <p className="mb-0 text-muted small">{player.email || 'No email provided'}</p>
                                <p className="mb-0 text-muted small">
                                  Category: {getPlayerCategoryLabel(tournamentId, player.id)}
                                </p>
                              </div>
                              <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
                                <select
                                  className="form-select form-select-sm"
                                  value={playerAssignments[normalizeId(player.id)] || ''}
                                  onChange={(event) => handleAssignCategory(tournamentId, player.id, event.target.value)}
                                  disabled={!isActive || isSavingCategory}
                                  style={{ minWidth: '200px' }}
                                >
                                  <option value="">No category</option>
                                  {categoriesForTournament.map((category) => (
                                    <option key={category.id} value={category.id}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveSelectedPlayer(tournamentId, player.id)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="d-grid gap-1">
                            <p className="small fw-semibold mb-1">Selected players by category</p>
                            <div className="d-flex flex-wrap gap-2">
                              {selectedCategories.length === 0 && (
                                <span className="text-muted small">No categories assigned yet.</span>
                              )}
                              {selectedCategories.map((category) => (
                                <span
                                  key={category.name || category.role || 'uncategorized'}
                                  className="badge bg-primary-subtle text-primary-emphasis"
                                >
                                  {category.name || category.role} • {category.players.length}
                                  <span className="text-muted ms-1">
                                    {category.players
                                      .map((player) => player.name || player.fullName || player.email)
                                      .join(', ')}
                                  </span>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => handlePlayerAction(tournamentId, 'add')}
                      disabled={isSaving || !isActive}
                    >
                      {isSaving ? 'Saving...' : 'Add to tournament'}
                    </button>
                    <button
                      className="btn btn-outline-primary"
                      type="button"
                      onClick={() => handlePlayerAction(tournamentId, 'invite')}
                      disabled={isSaving || !isActive}
                    >
                      {isSaving ? 'Saving...' : 'Invite players'}
                    </button>
                    <button
                      className="btn btn-link text-decoration-none"
                      type="button"
                      onClick={() => loadEligiblePlayers(tournamentId)}
                      disabled={isLoadingPlayers}
                    >
                      Refresh player list
                    </button>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        onClick={() => handleArchiveTournament(tournamentId, tournament.name, isActive)}
                        disabled={isArchiving}
                      >
                        {isArchiving ? 'Deleting...' : isActive ? 'Delete tournament' : 'Delete permanently'}
                      </button>
                      {!isActive && (
                        <button
                          className="btn btn-outline-success"
                          type="button"
                          onClick={() => handleActivateTournament(tournamentId, tournament.name)}
                          disabled={isReactivating}
                        >
                          {isReactivating ? 'Restoring...' : 'Restore tournament'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeManagementTab === 'categories' && (
                <div className="d-grid gap-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <p className="small fw-semibold mb-1 mb-0">Player categories</p>
                    {isLoadingCategories && <span className="text-muted small">Loading categories...</span>}
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none"
                      onClick={() => loadPlayerCategories(tournamentId)}
                      disabled={isLoadingCategories}
                    >
                      Refresh categories
                    </button>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="New category name"
                      value={categoryDraft}
                      onChange={(event) =>
                        setCategoryDrafts((prev) => ({ ...prev, [tournamentId]: event.target.value }))
                      }
                      disabled={isSavingCategory || !isActive}
                      style={{ maxWidth: '260px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleCreateCategory(tournamentId)}
                      disabled={isSavingCategory || !isActive}
                    >
                      {isSavingCategory ? 'Saving...' : 'Add category'}
                    </button>
                  </div>
                  <div className="d-grid gap-2">
                    {categoriesForTournament.length === 0 && !isLoadingCategories && (
                      <span className="text-muted small">
                        No categories yet. Create one to organize selected players.
                      </span>
                    )}
                    {categoriesForTournament.map((category) => {
                      const categoryId = normalizeId(category.id);

                      return (
    <PageShell containerClassName="p-0">
                        <div key={category.id} className="d-flex align-items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={categoryEditValues[categoryId] ?? category.name}
                            onChange={(event) =>
                              setCategoryEdits((prev) => ({
                                ...prev,
                                [tournamentId]: {
                                  ...(prev[tournamentId] || {}),
                                  [categoryId]: event.target.value,
                                },
                              }))
                            }
                            style={{ maxWidth: '260px' }}
                            disabled={isSavingCategory || !isActive}
                          />
                          <span className="badge text-bg-light">
                            {category.players?.length || 0} players
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleRenameCategory(tournamentId, categoryId)}
                            disabled={isSavingCategory || !isActive}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCategory(tournamentId, categoryId)}
                            disabled={isSavingCategory || !isActive}
                          >
                            Remove
                          </button>
                        </div>
                      
    </PageShell>
  );
                    })}
                  </div>
                </div>
              )}
              {status?.message && (
                <div
                  className={`alert alert-${
                    status.tone === 'success' ? 'success' : status.tone === 'danger' ? 'danger' : 'info'
                  } py-2 mb-0`}
                >
                  {status.message}
                </div>
              )}
              {archiveMessage?.message && (
                <div
                  className={`alert alert-${
                    archiveMessage.tone === 'success'
                      ? 'success'
                      : archiveMessage.tone === 'danger'
                        ? 'danger'
                        : 'info'
                  } py-2 mb-0`}
                >
                  {archiveMessage.message}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlayerManagementPage;
